(() => {
  const VALID_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const VALID_FINISHES = ["Gold", "Silver"];
  const VALID_STONES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const MONTH_NAMES = {
    Jan: "January", Feb: "February", Mar: "March", Apr: "April", May: "May", Jun: "June",
    Jul: "July", Aug: "August", Sep: "September", Oct: "October", Nov: "November", Dec: "December"
  };

  function init(root) {
    if (!root || root.dataset.loweiReady === "true") return;
    root.dataset.loweiReady = "true";

    const configNode = root.querySelector("[data-lowei-config]");
    const form = root.querySelector("form");
    if (!configNode || !form) return;

    const config = JSON.parse(configNode.textContent);
    const params = new URLSearchParams(location.search);
    const variantParam = params.get("variant");
    const deepLinkedVariant = variantParam && String(config.selectedVariant?.id) === variantParam
      ? config.selectedVariant
      : null;
    const optionKey = (name) => String(name || "").trim().toLowerCase();
    const initialIndex = config.optionNames.findIndex((name) => ["initial", "letter"].includes(optionKey(name)));
    const finishIndex = config.optionNames.findIndex((name) => ["finish", "color", "colour"].includes(optionKey(name)));
    const birthstoneIndex = config.optionNames.findIndex((name) => ["birthstone", "stone"].includes(optionKey(name)));
    const deepLinkedLetter = deepLinkedVariant?.options?.[initialIndex];
    const deepLinkedFinish = deepLinkedVariant?.options?.[finishIndex];
    const deepLinkedStone = deepLinkedVariant?.options?.[birthstoneIndex];
    const requestedLetter = (params.get("letter") || deepLinkedLetter || "A").toUpperCase();
    const requestedFinish = params.get("finish") || deepLinkedFinish || "Silver";
    const requestedStone = (params.get("stone") || deepLinkedStone || "").slice(0, 3);
    const state = {
      letter: VALID_LETTERS.includes(requestedLetter) ? requestedLetter : "A",
      finish: VALID_FINISHES.find((finish) => finish.toLowerCase() === String(requestedFinish).toLowerCase()) || "Silver",
      stone: VALID_STONES.find((stone) => stone.toLowerCase() === requestedStone.toLowerCase()) || null,
      stoneName: "",
      quantity: 1,
      variant: deepLinkedVariant,
      resolvingVariant: false
    };
    let variantRequest = 0;
    const trackingState = { started: false, completed: Boolean(state.stone) };

    function track(eventName, fields = {}) {
      const detail = { event: eventName, ...fields };
      root.dispatchEvent(new CustomEvent("lowei:analytics", { bubbles: true, detail }));
      if (config.analyticsEnabled && Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
    }

    function trackStart() {
      if (trackingState.started) return;
      trackingState.started = true;
      track("lowei_customizer_start");
    }

    if (state.stone) {
      state.stoneName = root.querySelector(`[data-stone="${state.stone}"]`)?.dataset.stoneName || "";
    }

    const el = {
      image: root.querySelector("[data-preview-image]"),
      stoneOverlay: root.querySelector("[data-preview-stone-overlay]"),
      loading: root.querySelector("[data-preview-loading]"),
      stage: root.querySelector("[data-preview-stage]"),
      quality: root.querySelector("[data-preview-quality]"),
      disclosure: root.querySelector("[data-preview-disclosure]"),
      variantId: root.querySelector("[data-variant-id]"),
      initial: root.querySelector("[data-initial-property]"),
      finish: root.querySelector("[data-finish-property]"),
      birthstone: root.querySelector("[data-birthstone-property]"),
      previewProperty: root.querySelector("[data-preview-property]"),
      error: root.querySelector("[data-selection-error]"),
      birthstoneGroup: root.querySelector("[data-birthstone-group]"),
      quantityInput: root.querySelector("[data-quantity-input]"),
      quantityOutput: root.querySelector("[data-quantity-output]"),
      stoneDetail: root.querySelector("[data-stone-detail]"),
      add: root.querySelector("[data-add-button]"),
      mobileAdd: root.querySelector("[data-mobile-add-button]"),
      status: root.querySelector("[data-status-message]")
    };

    function setVariantId(value) {
      const selectedVariantId = value ? String(value) : "";
      let input = form.querySelector('input[name="id"]');
      if (!input && selectedVariantId) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "id";
        input.dataset.variantId = "";
        form.prepend(input);
      }
      if (input) {
        input.value = selectedVariantId;
        input.setAttribute("value", selectedVariantId);
      }
      el.variantId = input;
    }

    function selectedValueForOption(optionName) {
      const key = optionKey(optionName);
      if (["initial", "letter"].includes(key)) return state.letter;
      if (["finish", "color", "colour"].includes(key)) return state.finish;
      if (["birthstone", "stone"].includes(key)) return state.stone ? MONTH_NAMES[state.stone] : "";
      return "";
    }

    function selectionOptionValueIds() {
      return config.optionValues.map((option) => {
        const selectedValue = selectedValueForOption(option.name);
        const match = option.values.find((value) => {
          if (["birthstone", "stone"].includes(optionKey(option.name))) {
            return optionKey(value.name).slice(0, 3) === optionKey(selectedValue).slice(0, 3);
          }
          return optionKey(value.name) === optionKey(selectedValue);
        });
        return match?.id ? String(match.id) : "";
      });
    }

    async function resolveVariant() {
      const requestId = ++variantRequest;
      const optionValueIds = selectionOptionValueIds();
      state.variant = null;

      if (!state.stone || optionValueIds.some((id) => !id)) {
        state.resolvingVariant = false;
        updatePurchaseUI();
        updateUrl();
        return null;
      }

      state.resolvingVariant = true;
      updatePurchaseUI();

      try {
        const requestUrl = new URL(location.href);
        ["variant", "letter", "finish", "stone", "section_id", "option_values"].forEach((key) => requestUrl.searchParams.delete(key));
        requestUrl.searchParams.set("section_id", config.sectionId);
        requestUrl.searchParams.set("option_values", optionValueIds.join(","));
        const response = await fetch(requestUrl, { headers: { Accept: "text/html" } });
        if (!response.ok) throw new Error(`Variant lookup failed (${response.status})`);
        const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
        const responseConfig = responseDocument.querySelector("[data-lowei-config]");
        if (!responseConfig) throw new Error("Variant lookup returned no configuration");
        const resolvedVariant = JSON.parse(responseConfig.textContent).selectedVariant;
        if (requestId !== variantRequest) return null;
        state.variant = resolvedVariant;
        state.resolvingVariant = false;
        el.status.textContent = resolvedVariant ? "Design ready to add." : "This combination is unavailable.";
        update();
        return resolvedVariant;
      } catch (_) {
        if (requestId !== variantRequest) return null;
        state.variant = null;
        state.resolvingVariant = false;
        el.status.textContent = "We could not verify this combination. Please try again.";
        updatePurchaseUI();
        updateUrl();
        return null;
      }
    }

    function selectButtons(selector, dataKey, value) {
      const buttons = [...root.querySelectorAll(selector)];
      buttons.forEach((button, index) => {
        const selected = button.dataset[dataKey] === value;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-checked", String(selected));
        button.tabIndex = selected || (!value && index === 0) ? 0 : -1;
      });
    }

    function updateUrl() {
      const url = new URL(location.href);
      url.searchParams.set("letter", state.letter);
      url.searchParams.set("finish", state.finish.toLowerCase());
      const optionValueIds = selectionOptionValueIds();
      if (state.variant) url.searchParams.set("variant", state.variant.id);
      else url.searchParams.delete("variant");
      if (state.stone && optionValueIds.every(Boolean)) url.searchParams.set("option_values", optionValueIds.join(","));
      else url.searchParams.delete("option_values");
      if (state.stone) url.searchParams.set("stone", state.stone.toLowerCase());
      else url.searchParams.delete("stone");
      history.replaceState({}, "", url);
    }

    function formatTotal() {
      const currency = root.dataset.currency || "USD";
      const locale = document.documentElement.lang || "en-US";
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format((Number(root.dataset.unitPrice) * state.quantity) / 100);
    }

    function updatePurchaseUI(variant = state.variant) {
      const total = formatTotal();
      el.quantityInput.value = String(state.quantity);
      el.quantityOutput.textContent = String(state.quantity);
      root.querySelector('[data-quantity-action="decrease"]').disabled = state.quantity === 1;
      root.querySelector('[data-quantity-action="increase"]').disabled = state.quantity === 10;

      if (!state.stone) {
        el.add.disabled = true;
        el.add.textContent = "Choose a birthstone";
        el.mobileAdd.disabled = true;
        el.mobileAdd.textContent = "Complete design";
      } else if (state.resolvingVariant) {
        el.add.disabled = true;
        el.add.textContent = "Checking availability…";
        el.mobileAdd.disabled = true;
        el.mobileAdd.textContent = "Checking…";
      } else if (!variant || !variant.available) {
        el.add.disabled = true;
        el.add.textContent = "Currently unavailable";
        el.mobileAdd.disabled = true;
        el.mobileAdd.textContent = "Unavailable";
      } else {
        el.add.disabled = false;
        el.add.textContent = state.stone
          ? `${state.quantity > 1 ? `Add ${state.quantity} designs` : "Add my design"} · ${total}`
          : "Choose a birthstone";
        el.mobileAdd.disabled = false;
        el.mobileAdd.textContent = state.stone
          ? `${state.quantity > 1 ? `Add ${state.quantity}` : "Add"} · ${total}`
          : "Complete design";
      }
    }

    function update() {
      const calibratedKey = state.stone ? `${state.finish}|${state.letter}|${state.stone}` : "";
      const calibratedAsset = config.calibratedAssets[calibratedKey];
      const finishAssets = state.finish === "Gold" ? config.goldAssets : config.silverAssets;
      const previewAsset = calibratedAsset || finishAssets[state.letter];
      const variant = state.variant;

      el.loading.hidden = false;
      el.stage.setAttribute("aria-busy", "true");
      el.stage.classList.remove("has-image-error");
      el.image.hidden = false;
      const selectedStoneButton = state.stone ? root.querySelector(`[data-stone="${state.stone}"]`) : null;
      const selectedStoneColor = selectedStoneButton?.querySelector("i")?.style.getPropertyValue("--stone") || "";
      el.stoneOverlay.toggleAttribute("hidden", !state.stone || Boolean(calibratedAsset));
      if (selectedStoneColor && !calibratedAsset) el.stoneOverlay.style.setProperty("--preview-stone", selectedStoneColor);
      el.image.src = previewAsset;
      el.image.alt = calibratedAsset
        ? `${state.finish} initial ${state.letter} necklace with ${state.stoneName} birthstone`
        : `${state.finish} initial ${state.letter} necklace with the selected birthstone colour previewed on the crystal`;
      el.image.onload = () => {
        el.loading.hidden = true;
        el.stage.setAttribute("aria-busy", "false");
        el.stage.classList.remove("has-image-error");
        el.image.hidden = false;
      };
      el.image.onerror = () => {
        el.image.onerror = null;
        el.loading.hidden = true;
        el.stage.setAttribute("aria-busy", "false");
        el.image.hidden = true;
        el.stoneOverlay.setAttribute("hidden", "");
        el.stage.classList.add("has-image-error");
        el.quality.textContent = "PREVIEW UNAVAILABLE";
        el.disclosure.textContent = "Preview unavailable. Please use the product gallery before ordering.";
      };

      el.stage.dataset.status = calibratedAsset ? "calibrated" : "reference";
      if (calibratedAsset) {
        el.quality.textContent = "CALIBRATED PROOF";
        el.disclosure.textContent = "Assembled product reference with a calibrated birthstone colour proof.";
      } else if (state.finish === "Gold") {
        el.quality.textContent = "FINISH-MATCHED GOLD PREVIEW";
        el.disclosure.textContent = state.stone
          ? "Gold finish preview based on the real assembly and matching Gold initial photography; the selected birthstone colour is previewed live."
          : "Gold finish preview based on the real assembly and matching Gold initial photography. Choose a birthstone to complete the design.";
      } else {
        el.quality.textContent = "ASSEMBLY-BASED PREVIEW";
        el.disclosure.textContent = "Silver preview based on the assembled product photograph; the selected birthstone colour is previewed live.";
      }

      root.querySelector("[data-summary-letter]").textContent = state.letter;
      root.querySelector("[data-summary-finish]").textContent = state.finish;
      root.querySelector("[data-summary-stone]").textContent = state.stone ? `${state.stone} · ${state.stoneName}` : "Choose one";
      root.querySelector("[data-purchase-summary]").textContent = `${state.finish} initial ${state.letter} · ${state.stone ? `${state.stone} ${state.stoneName}` : "Choose a birthstone"}`;
      const completedChoices = state.stone ? 3 : 2;
      const progress = root.querySelector("[data-progress]");
      progress.setAttribute("aria-valuenow", String(completedChoices));
      root.querySelector("[data-progress-copy]").textContent = completedChoices === 3 ? "Design complete" : `${completedChoices} of 3 choices complete`;
      root.querySelector("[data-progress-fill]").style.width = `${(completedChoices / 3) * 100}%`;
      root.querySelector("[data-review-letter]").textContent = state.letter;
      root.querySelector("[data-review-finish]").textContent = state.finish;
      root.querySelector("[data-review-stone]").textContent = state.stone ? `${state.stone} · ${state.stoneName}` : "Not selected";
      const stoneReview = root.querySelector("[data-review-stone-row]");
      stoneReview.classList.toggle("is-pending", !state.stone);
      stoneReview.querySelector("i").textContent = state.stone ? "✓" : "·";
      root.querySelector("[data-mobile-summary]").textContent = `${state.finish} ${state.letter} · ${state.stone || "Choose stone"}`;
      el.stoneDetail.hidden = !state.stone;
      if (state.stone) {
        const selectedStone = root.querySelector(`[data-stone="${state.stone}"]`);
        el.stoneDetail.querySelector("[data-stone-detail-dot]").style.setProperty("--stone", selectedStone.querySelector("i").style.getPropertyValue("--stone"));
        el.stoneDetail.querySelector("[data-stone-detail-dot]").style.background = "radial-gradient(circle at 32% 28%, #fff 0 7%, var(--stone) 22%, #332d2a 145%)";
        el.stoneDetail.querySelector("[data-stone-detail-month]").textContent = `${MONTH_NAMES[state.stone].toUpperCase()} BIRTHSTONE`;
        el.stoneDetail.querySelector("[data-stone-detail-title]").textContent = `${state.stoneName} crystal`;
        el.stoneDetail.querySelector("[data-stone-detail-description]").textContent = `Previewed with initial ${state.letter} in ${state.finish}.`;
      }

      selectButtons("[data-letter]", "letter", state.letter);
      selectButtons("[data-finish]", "finish", state.finish);
      selectButtons("[data-stone]", "stone", state.stone || "");

      setVariantId(variant?.id);
      el.initial.value = state.letter;
      el.finish.value = state.finish;
      el.birthstone.value = state.stone ? `${state.stone} · ${state.stoneName} crystal` : "";
      el.previewProperty.value = state.stone ? `${state.letter}|${state.finish}|${state.stone} ${state.stoneName}` : "";
      updatePurchaseUI(variant);
      updateUrl();
    }

    root.addEventListener("click", async (event) => {
      const letter = event.target.closest("[data-letter]");
      const finish = event.target.closest("[data-finish]");
      const stone = event.target.closest("[data-stone]");
      const quantity = event.target.closest("[data-quantity-action]");
      const selectedOption = letter ? "initial" : finish ? "finish" : stone ? "birthstone" : null;

      if (selectedOption) trackStart();
      if (letter) state.letter = letter.dataset.letter;
      if (finish) state.finish = finish.dataset.finish;
      if (stone) {
        state.stone = stone.dataset.stone;
        state.stoneName = stone.dataset.stoneName;
        el.error.hidden = true;
        el.birthstoneGroup.setAttribute("aria-invalid", "false");
      }
      if (letter || finish || stone) {
        variantRequest++;
        state.variant = null;
        state.resolvingVariant = Boolean(state.stone);
        update();
        track("lowei_option_selected", {
          option_name: selectedOption,
          option_value: selectedOption === "initial" ? "letter_selected" : selectedOption === "finish" ? state.finish : state.stone,
          design_complete: Boolean(state.stone)
        });
        if (state.stone && !trackingState.completed) {
          trackingState.completed = true;
          track("lowei_customizer_complete", { finish: state.finish, birthstone_month: state.stone });
        }
        if (state.stone) await resolveVariant();
      }
      if (quantity) {
        state.quantity = quantity.dataset.quantityAction === "increase"
          ? Math.min(10, state.quantity + 1)
          : Math.max(1, state.quantity - 1);
        updatePurchaseUI();
      }

      if (event.target.closest("[data-reset-button]")) {
        state.letter = "A";
        state.finish = "Silver";
        state.stone = null;
        state.stoneName = "";
        state.quantity = 1;
        state.variant = null;
        state.resolvingVariant = false;
        variantRequest++;
        trackingState.completed = false;
        el.error.hidden = true;
        el.birthstoneGroup.setAttribute("aria-invalid", "false");
        el.status.textContent = "Design reset.";
        update();
        track("lowei_customizer_reset");
      }

      if (event.target.closest("[data-mobile-add-button]")) form.requestSubmit();

      if (event.target.closest("[data-share-button]")) {
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url: location.href });
            el.status.textContent = "Design link shared.";
            track("lowei_design_share", { method: "native_share" });
          } else if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(location.href);
            el.status.textContent = "Design link copied.";
            track("lowei_design_share", { method: "clipboard" });
          } else {
            const fallback = document.createElement("textarea");
            fallback.value = location.href;
            fallback.setAttribute("readonly", "");
            fallback.style.position = "fixed";
            fallback.style.opacity = "0";
            document.body.appendChild(fallback);
            fallback.select();
            const copied = document.execCommand("copy");
            fallback.remove();
            if (!copied) throw new Error("Copy unavailable");
            el.status.textContent = "Design link copied.";
            track("lowei_design_share", { method: "legacy_clipboard" });
          }
        } catch (_) {
          el.status.textContent = "Sharing was cancelled. You can copy the address from your browser.";
        }
      }
    });

    root.addEventListener("keydown", (event) => {
      const current = event.target.closest('[role="radio"]:not([disabled])');
      if (!current || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
      const group = current.closest('[role="radiogroup"]');
      const buttons = [...group.querySelectorAll('[role="radio"]:not([disabled])')];
      const direction = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1;
      const next = event.key === "Home"
        ? buttons[0]
        : event.key === "End"
          ? buttons[buttons.length - 1]
          : buttons[(buttons.indexOf(current) + direction + buttons.length) % buttons.length];
      event.preventDefault();
      next.focus();
      next.click();
    });

    form.addEventListener("submit", async (event) => {
      if (!state.stone) {
        event.preventDefault();
        trackStart();
        track("lowei_customizer_validation_error", { reason: "birthstone_required" });
        el.error.hidden = false;
        el.birthstoneGroup.setAttribute("aria-invalid", "true");
        const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
        root.querySelector('[data-step="birthstone"]').scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
        root.querySelector("[data-stone]").focus({ preventScroll: true });
      } else if (state.resolvingVariant || !state.variant) {
        event.preventDefault();
        const variant = await resolveVariant();
        if (variant?.available) form.requestSubmit();
      } else {
        setVariantId(state.variant.id);
        track("lowei_add_to_cart_submit", { finish: state.finish, birthstone_month: state.stone, quantity: state.quantity });
        el.add.setAttribute("aria-busy", "true");
        el.add.textContent = "Adding…";
      }
    });

    root.querySelectorAll(".lowei-customizer__gift-guide, .lowei-customizer__information details").forEach((details) => {
      details.addEventListener("toggle", () => {
        if (!details.open) return;
        const topic = details.querySelector("summary")?.textContent.replace("+", "").trim() || "unknown";
        track("lowei_help_open", { topic });
      });
    });

    update();
    if (state.stone && !state.variant) resolveVariant();
    track("lowei_customizer_view", { restored_design: Boolean(state.stone) });
  }

  function initAll(scope = document) {
    scope.querySelectorAll("[data-lowei-customizer]").forEach(init);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initAll());
  else initAll();

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
